import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { ICustomerFulfillment } from "@/data/model/Customer";
import { FulfillmentPositionDisplayMode, FulfillmentProductionState, FulfillmentState } from "@/data/model/Enums";

import { logError } from "@/shared/logging/errors";
import { Ripple } from "@/shared/ui/Ripple";

import { TastierClient } from "@/app/TastierClient";
import type { AppContext } from "@/app/context/AppContext";
import { trans } from "@/app/localization/Trans";
import { Popup } from "@/app/ui/Popup";
import { CustomerFulfillmentHelper } from "@/app/util/CustomerFulfillmentHelper";

export class RealtimeFulfillmentList {
    private $element: JQuery;
    private timer?: number;

    private orderId: string;
    private allowFulfill: boolean;
    private showFulfillmentId: string | undefined;

    private orderFulfillments?: Array<ICustomerFulfillment>;

    private context: AppContext;
    private customerDao: CustomerDao;

    public onUpdateTime?: { (time: Date) };
    public onListChanged?: { () };

    constructor(forOrder: string, allowFulfill: boolean = true, showFulfillmentId: string | undefined = undefined) {
        this.context = TastierClient.context;
        this.customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

        this.$element = $('<div class="fulfillmentlist scrollable-element" />');
        this.orderId = forOrder;
        this.allowFulfill = allowFulfill;
        this.showFulfillmentId = showFulfillmentId;
    }

    public renderTo($element: JQuery): RealtimeFulfillmentList {
        $element.append(this.$element);
        return this;
    }

    public startListening() {
        this.context.onFulfillmentsChanged.add(this.onFulfillmentsChanged, this);
        this.context.loadFulfillmentsIfNeeded();

        this.onFulfillmentsChanged();
        this.startTimer();
    }

    public stopListening() {
        this.context.onFulfillmentsChanged.remove(this.onFulfillmentsChanged, this);
        this.cancelTimer();
    }

    public hasAnyFulfillment(): boolean {
        return this.orderFulfillments != null && this.orderFulfillments.length > 0;
    }

    public hasAnyFulfillmentReady(): boolean {
        if (this.orderFulfillments == null) {
            return false;
        } else {
            return this.orderFulfillments.some((fulfillment) => CustomerFulfillmentHelper.isReady(fulfillment));
        }
    }

    private updateFulfillments(sort: boolean) {
        const currentTime = new Date();

        if (this.onUpdateTime !== undefined) this.onUpdateTime(currentTime);

        let hasListChanges = false;
        const handledIds = new Array<string>();
        this.$element.children().each((i, e) => {
            const row = $(e);

            const model: ICustomerFulfillment = row.data("model");
            if (model == null) {
                row.remove();
                hasListChanges = true;
                return true;
            }
            
            const ful = this.orderFulfillments!.find((c) => c.id === model.id);
            if (ful === undefined) {
                row.remove();
                hasListChanges = true;
                return true;
            }

            if (sort) row.data("model", ful);

            //udpate time and state
            hasListChanges = hasListChanges || (!hasListChanges && this.setStateAndTime(row, ful));
            handledIds.push(model.id);
        });

        for (const ful of this.orderFulfillments!) {
            //already updated
            if (handledIds.includes(ful.id)) continue;

            //add new row
            const row = $(
                '<div class="fulfillment"><div class="item"><div class="quantity">' +
                    ful.articles +
                    "x</div>" +
                    CustomerFulfillmentHelper.getFulfillmentGroupName(ful) +
                    '</div><div class="state"></div></div>'
            )
                .data("model", ful)
                .appendTo(this.$element);

            this.setStateAndTime(row, ful);
            hasListChanges = true;
        }

        if (sort) {
            this.$element
                .find("> .fulfillment")
                ["sort"]((a, b) => {
                    return -CustomerFulfillmentHelper.newestFirstComparator($(a).data("model"), $(b).data("model"));
                })
                .appendTo(this.$element);
        }

        if (hasListChanges && this.onListChanged !== undefined) {
            this.onListChanged();
        }
    }

    private setStateAndTime($row: JQuery, fulfillment: ICustomerFulfillment): boolean {
        let stateChanged = false;
        const stateCont = $row.find("> .state");

        const productionState = CustomerFulfillmentHelper.getFulfillmentProductionState(fulfillment);
        const newColor = CustomerFulfillmentHelper.colorsMap[productionState];

        if (fulfillment.isFulfilled) {
            if (!stateCont.hasClass("fulfilled")) {
                stateCont.parent().find("> .fulfillbtn").remove();
                stateCont.removeClass("green red orange").addClass("fulfilled").html('<i class="fas fa-check"></i>');
                stateChanged = true;
            }
        } else {
            if (this.shouldDisplayReadyAnimation(fulfillment)) {
                const pare = stateCont.parent();
                if (pare.find("> .checkanimation").length === 0) {
                    const anim = $('<div class="checkanimation"><i class="fal fa-check"></i></div>').appendTo(pare);
                    anim[0].offsetWidth;
                    anim.addClass("animate")
                        .delay(2000)
                        .queue((e) => {
                            anim.remove();
                            e();
                        });
                }
            }

            if (this.allowFulfill && !stateCont.hasClass(newColor)) {
                stateCont.removeClass("green red orange").addClass(newColor);

                //check fulfill button
                const existingBtn = stateCont.parent().find("> .fulfillbtn");
                if (productionState == FulfillmentProductionState.Ready && existingBtn.length === 0) {
                    Ripple.bind(
                        $(
                            '<button class="fulfillbtn"><i class="fa-light fa-person-carry-box"></i><i class="fal fa-check"></i></button>'
                        ).click((e) => {
                            const btn = $(e.currentTarget);
                            const fulfillment: ICustomerFulfillment = btn.parent().data("model");

                            const message = fulfillment.isLinked
                                ? trans.fulfillmentPopover.didYouGetWholeOrder
                                : CustomerFulfillmentHelper.getFulfillmentGroupName(fulfillment) +
                                  ": " +
                                  trans.fulfillmentPopover.didYouGetEverything;

                            Popup.show(
                                message,
                                async () => {
                                    btn.prop("disabled", true);

                                    try {
                                        await this.customerDao.receiveFulfillment(fulfillment.id, {
                                            orderId: fulfillment.orderId,
                                            socketConnectionId: this.context.socket.connectionId!,
                                        });
                                        this.context.removeFulfillment(fulfillment.id);
                                    } catch (error) {
                                        logError(error);
                                        btn.prop("disabled", false);
                                    }
                                },
                                trans.fulfillmentPopover.yesGotIt,
                                trans.general.cancel,
                                undefined,
                                undefined,
                                true,
                                true
                            );
                        })
                    ).insertBefore(stateCont);
                } else if (CustomerFulfillmentHelper.isPreparationState(productionState) && existingBtn.length !== 0) {
                    existingBtn.remove();
                }

                stateChanged = true;
            }

            if (this.allowFulfill) {
                const icon = CustomerFulfillmentHelper.getIcon(fulfillment, productionState);
                const indicatorValue = CustomerFulfillmentHelper.getQueueIndicatorValue(fulfillment, productionState);
                stateCont.html(
                    indicatorValue === ""
                        ? ""
                        : `<div class="time"><i class="fal ${icon}"></i>${CustomerFulfillmentHelper.getQueueIndicatorValue(fulfillment, productionState)}</div>`
                );
            }
        }
        return stateChanged;
    }

    private shouldDisplayReadyAnimation(fulfillment: ICustomerFulfillment) {
        const timeDif = CustomerFulfillmentHelper.remainingFulfillmentTime(fulfillment);
        if (
            fulfillment.fulfillmentPositionDisplayMode == FulfillmentPositionDisplayMode.Duration ||
            fulfillment.state == FulfillmentState.Ready
        ) {
            return -499 <= timeDif && timeDif < 500;
        }

        return false;
    }

    private onFulfillmentsChanged() {
        const orderFulfillments =
            this.context.currentFulfillments === undefined
                ? []
                : this.context.currentFulfillments.filter((c) => c.orderId === this.orderId);

        if (this.showFulfillmentId != null) {
            this.orderFulfillments = orderFulfillments.filter((f) => f.id === this.showFulfillmentId);
        } else {
            this.orderFulfillments = orderFulfillments;
        }

        this.updateFulfillments(true);
    }

    private cancelTimer() {
        if (this.timer === undefined) return;

        clearInterval(this.timer);
        this.timer = undefined;
    }

    private startTimer() {
        if (this.timer !== undefined) clearInterval(this.timer);

        this.timer = window.setInterval(() => {
            this.updateFulfillments(false);
        }, 1000);
    }
}
